import React from 'react';
import { Tooltip } from '@mui/material';
import StatusChip from './StatusChip';
import { COLORS, RADIUS } from './tokens';
import { formatDisplayWeek } from '../../../helper';

/*
 * "Not approved" marker for report week lines (T1.35, 2026-10-26).
 *
 * A week is marked when ANY Weekly Sale, Shop Payment or Account Payment in that week — across
 * all shops and accounts, not just the ones the report was run for — is not APPROVED. The data
 * comes from the report endpoint's `unapprovedWeeks` key (helper/UnapprovedWeeks.php). It is a
 * marker only: no figure depends on it.
 *
 *   <NotApprovedTag info={map.get(weekCode)} />   on a week line — renders nothing when undefined
 *   <NotApprovedRange weeks={unapprovedWeeks} />  on a view with no week lines (a summary)
 *
 * Styled inline rather than through admin-theme.css's `.pc-admin .pc-chip--warning`, because a
 * printed report is rendered outside `.pc-admin` and would lose the colours. The tooltip is
 * screen-only; the print shows the pill.
 */

const PILL = {
    background: COLORS.warningBg,
    color: COLORS.warningText,
    border: '1px solid #fde68a',
    borderRadius: RADIUS.chip,
    padding: '1px 8px',
    fontSize: 11,
    fontWeight: 600,
    lineHeight: '16px',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
    marginLeft: 8,
    display: 'inline-flex',
    alignItems: 'center',
    cursor: 'default',
    WebkitPrintColorAdjust: 'exact',
    printColorAdjust: 'exact',
};

// "Weekly Sale 13 · Shop Payments 13 · Account Payments 6" — non-zero parts only.
export function notApprovedBreakdown(info) {
    if (!info) return '';
    return [
        ['Weekly Sale', info.WEEKLY_SALE],
        ['Shop Payments', info.SHOP_PAYMENT],
        ['Account Payments', info.ACCOUNT_PAYMENT],
    ]
        .filter(([, n]) => Number(n) > 0)
        .map(([label, n]) => `${label} ${n}`)
        .join(' · ');
}

function Pill({ text }) {
    return (
        <Tooltip title={text} arrow>
            <span aria-label={text} style={{ display: 'inline-flex' }}>
                <StatusChip label="Not approved" type="warning" style={PILL} />
            </span>
        </Tooltip>
    );
}

export default function NotApprovedTag({ info }) {
    const breakdown = notApprovedBreakdown(info);
    if (!breakdown) return null;
    return <Pill text={`Not approved this week (all shops): ${breakdown}`} />;
}

export function NotApprovedRange({ weeks }) {
    const list = (weeks || []).filter(w => notApprovedBreakdown(w));
    if (list.length === 0) return null;
    const text = `Not approved records in the selected weeks (all shops): `
        + list.map(w => `${formatDisplayWeek(w.WEEK)} — ${notApprovedBreakdown(w)}`).join('; ');
    return <Pill text={text} />;
}
